import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { UserService } from '@services/user.service';
import { Observable } from 'rxjs';
import { IWorktask, IWorktaskResponse, IWorktasks } from './worktask.service';

export interface IMesUser {
	id: string;
	alias: string;
	firstName: string;
	lastName: string;
	isActive: boolean;
	salaryCode: string;
	teamProfileId: string;
	departmentId: string;
	firstWorkDate: string | null;
}

export interface IMesUserResponse {
	result: IMesUser;
}

export interface IMesUsers {
	result: IMesUser[];
}

interface IPostEmployeeRequest {
	alias: string;
	firstName: string;
	lastName: string;
	isActive: boolean;
	salaryCode: string;
	teamProfileId: string;
	departmentId: string;
	firstWorkDate: string | null;
}

@Injectable({
	providedIn: 'root',
})
export class EmployeeService {
	constructor(
		private http: HttpClient,
		private userService: UserService,
	) {
		this.userService.accountChanged$.subscribe(() => (this.currentEmployee = null));
	}

	private _currentEmployee: IMesUser;
	public get currentEmployee(): IMesUser {
		return this._currentEmployee;
	}

	public set currentEmployee(value: IMesUser) {
		this._currentEmployee = value;
		this.favouriteWorkTasks = [];
		if (value != null) {
			this.getFavouriteWorkTasks(value.id, 6, 100).subscribe((c) => (this.favouriteWorkTasks = c.result));
		}
	}

	public favouriteWorkTasks: IWorktask[];

	getEmployees(): Observable<IMesUsers> {
		return this.http.get<IMesUsers>(`${environment.awsEnvUri}/mes/employees`, {
			headers: this.userService.getHeaders(),
		});
	}

	getEmployeeBySalaryKey(alias): Observable<IMesUserResponse> {
		return this.http.get<IMesUserResponse>(`${environment.awsEnvUri}/mes/employees/alias/${alias}`, { headers: this.userService.getHeaders() });
	}

	checkIn(employeeId: any, workTaskId: any): Observable<string> {
		return this.http.post<string>(`${environment.awsEnvUri}/mes/employees/${employeeId}/worktasks/${workTaskId}`, {}, { headers: this.userService.getHeaders() });
	}

	checkOut(employeeId: string): Observable<string> {
		return this.http.post<string>(`${environment.awsEnvUri}/mes/employees/${employeeId}/worktasks/00000000-0000-0000-0000-000000000000`, {}, { headers: this.userService.getHeaders() });
	}

	getActiveWorkTask(employeeId: string): Observable<IWorktaskResponse> {
		return this.http.get<IWorktaskResponse>(`${environment.awsEnvUri}/mes/employees/${employeeId}/activeworktask`, { headers: this.userService.getHeaders() });
	}

	getFavouriteWorkTasks(employeeId: string, favouriteCount: number, sampleSize: number): Observable<IWorktasks> {
		return this.http.get<IWorktasks>(favouriteCount && sampleSize ? `${environment.awsEnvUri}/mes/employees/${employeeId}/favouriteworktasks?count=${favouriteCount}&sampleSize=${sampleSize}` : `${environment.awsEnvUri}/mes/employees/${employeeId}/favouriteworktasks`, { headers: this.userService.getHeaders() });
	}

	createEmployee(employee: IMesUser): Observable<IMesUserResponse> {
		const body: IPostEmployeeRequest = {
			alias: employee.alias,
			firstName: employee.firstName,
			lastName: employee.lastName,
			isActive: employee.isActive,
			salaryCode: employee.salaryCode,
			teamProfileId: employee.teamProfileId,
			departmentId: employee.departmentId,
			firstWorkDate: employee.firstWorkDate,
		};
		return this.http.post<IMesUserResponse>(`${environment.awsEnvUri}/mes/employees`, body, {
			headers: this.userService.getHeaders(),
		});
	}

	updateEmployee(employee: IMesUser): Observable<IMesUserResponse> {
		const body: IPostEmployeeRequest = {
			alias: employee.alias,
			firstName: employee.firstName,
			lastName: employee.lastName,
			isActive: employee.isActive,
			salaryCode: employee.salaryCode,
			teamProfileId: employee.teamProfileId,
			departmentId: employee.departmentId,
			firstWorkDate: employee.firstWorkDate,
		};
		return this.http.put<IMesUserResponse>(`${environment.awsEnvUri}/mes/employees/${employee.id}`, body, { headers: this.userService.getHeaders() });
	}

	deleteEmployee(employeeId: string): Observable<any> {
		return this.http.delete(`${environment.awsEnvUri}/mes/employees/${employeeId}`, {
			headers: this.userService.getHeaders(),
		});
	}

	getEmployeeFullName(employee: IMesUser): string {
		return `${employee.firstName} ${employee.lastName}`;
	}
}
